import { memo, useState } from 'react'
import { Button, Flex, Select, Typography } from 'antd'
import { EyeOutlined, EyeInvisibleOutlined, EditOutlined } from '@ant-design/icons'
import { PlantationPolygon } from '../../../constants/string'
import { useTranslation } from 'react-i18next'


const Label = memo(({ label, value }) => {
  return (
    <Flex gap='small' align='center' justify='space-between' flex={1} style={{ overflow: 'hidden' }}>
      <Typography.Text
        ellipsis={{ tooltip: label }}
        style={{
          flexGrow: 1,
          minWidth: 0,
          overflow: 'hidden'
        }}
      >
        {label}
      </Typography.Text>
      <div
        style={{
          background: PlantationPolygon.Colors[value],
          borderRadius: '50%',
          width: 16,
          aspectRatio: 1,
          flexShrink: 0
        }}
      />
    </Flex>
  )
})

const Option = memo(({ selected, visible, onVisibleChange, children }) => {
  const [internalVisible, setInternalVisible] = useState(visible ?? true)
  const isVisible = visible ?? internalVisible

  const toggleVisible = (e) => {
    e.stopPropagation()

    if (visible === undefined) setInternalVisible(!isVisible);
    onVisibleChange?.(!isVisible)
  }

  return (
    <Flex gap='small' align='center' justify='flex-start' style={{ opacity: selected || isVisible ? 1 : 0.4 }}>
      {selected ? (
        <EditOutlined style={{ marginLeft: 4, marginRight: 4 }}/>
      ) : (
        <Button
          type='text'
          size='small'
          icon={isVisible ? <EyeOutlined/> : <EyeInvisibleOutlined/>}
          onClick={toggleVisible}
          disabled={selected}
        />
      )}
      {children}
    </Flex> 
  )
})

const PolygonLayerSelect = ({ style, visibleLayers, onVisibleChange, value, onChange }) => {
  const { t } = useTranslation()
  const [internalValue, setInternalValue] = useState(value ?? PlantationPolygon.Types.TOTAL)
  const [internalVisibleLayers, setInternalVisibleLayers] = useState(visibleLayers ?? Object.values(PlantationPolygon.Types))

  const currentValue = value ?? internalValue
  const currentVisibleLayers = visibleLayers ?? internalVisibleLayers

  const handleSelectVisibleLayer = (visible, value) => {
    if (currentValue === value) return;
    const newVisibleLayers = visible ? [...currentVisibleLayers, value] : currentVisibleLayers.filter(v => v !== value)
    if (visibleLayers === undefined) setInternalVisibleLayers(newVisibleLayers);
    onVisibleChange?.(newVisibleLayers)
  }

  const handleValueChange = (newValue) => {
    const nextVisibleLayers = currentVisibleLayers.includes(newValue) 
      ? currentVisibleLayers 
      : [...currentVisibleLayers, newValue];
    
    if (value === undefined) setInternalValue(newValue);
    if (visibleLayers === undefined) setInternalVisibleLayers(nextVisibleLayers);

    onVisibleChange?.(nextVisibleLayers)
    onChange?.(newValue)
  }

  return (
    <Select
      autoComplete="off"
      placeholder={t("type")}
      style={style}
      value={currentValue}
      onChange={handleValueChange}
      options={Object.values(PlantationPolygon.Types).map(value => ({ value, label: t(PlantationPolygon.Labels[value]) }))}
      labelRender={({ label, value }) => <Label label={label} value={value}/>}
      optionRender={({ data: { label, value } }) => (
        <Option
          selected={currentValue === value}
          visible={currentVisibleLayers.includes(value)}
          onVisibleChange={(visible) => handleSelectVisibleLayer(visible, value)}
        >
          <Label label={label} value={value}/>
        </Option>
      )}
    />
  )
}

export default memo(PolygonLayerSelect)
